/**
 * La scheda Biglietti di un evento: i biglietti comprati sul sito, per
 * ordine, e quanti mancano da registrare.
 */

import type { BigliettoEvento, StatoLetturaOrdini, StatoPdf } from './tipi';
import type { RiassuntoScheda } from './riepilogo';

export interface ContoBiglietti {
  /** Venduti e non annullati. */
  validi: number;
  registrati: number;
  daRegistrare: number;
  annullati: number;
}

export function contaBigliettiEvento(biglietti: BigliettoEvento[]): ContoBiglietti {
  const annullati = biglietti.filter((b) => b.stato === 'annullato').length;
  const registrati = biglietti.filter((b) => b.stato === 'registrato').length;
  const validi = biglietti.length - annullati;
  return { validi, registrati, daRegistrare: validi - registrati, annullati };
}

export interface OrdineConBiglietti {
  ordine: BigliettoEvento['ordine'];
  biglietti: BigliettoEvento[];
}

/** I biglietti raggruppati per ordine, nell'ordine in cui arrivano (dal piu' recente). */
export function perOrdine(biglietti: BigliettoEvento[]): OrdineConBiglietti[] {
  const gruppi = new Map<number, OrdineConBiglietti>();
  for (const b of biglietti) {
    const gruppo = gruppi.get(b.ordine.id);
    if (gruppo) gruppo.biglietti.push(b);
    else gruppi.set(b.ordine.id, { ordine: b.ordine, biglietti: [b] });
  }
  return [...gruppi.values()];
}

/** Vero se nell'ordine c'e' da fare qualcosa: un biglietto ancora da registrare. */
export const ordineDaCompletare = (o: OrdineConBiglietti): boolean => o.biglietti.some((b) => b.stato === 'da_registrare');

/** Quello che la scheda Biglietti dice di se' nella barra delle schede. */
export function riassuntoBiglietti(collegato: boolean, conto: ContoBiglietti): RiassuntoScheda {
  if (!collegato) return { testo: 'Nessun prodotto del sito', fatto: false, avviso: false };
  if (conto.validi === 0) return { testo: 'Nessun biglietto venduto', fatto: false, avviso: false };
  return {
    testo: `${conto.registrati} su ${conto.validi} registrati`,
    avanzamento: conto.registrati / conto.validi,
    fatto: conto.daRegistrare === 0,
    avviso: false,
  };
}

function conCodice(urlRegistrazione: string | null, origine: string, nome: 'b' | 'o', token: string): string {
  const base = urlRegistrazione?.trim() || `${origine}/registrazione-evento`;
  return `${base}${base.includes('?') ? '&' : '?'}${nome}=${token}`;
}

/** Il link per registrare un biglietto, da copiare e mandare a chi lo usera'. */
export function linkRegistrazione(urlRegistrazione: string | null, token: string, origine: string): string {
  return conCodice(urlRegistrazione, origine, 'b', token);
}

/** La pagina dei biglietti di chi ha pagato (quella dell'email "registra i partecipanti"): tutti i biglietti dell'ordine. */
export function linkAcquirente(urlRegistrazione: string | null, token: string, origine: string): string {
  return conCodice(urlRegistrazione, origine, 'o', token);
}

/** Oltre questo tempo una lettura partita e mai finita e' un problema, non un ritardo. */
const MINUTI_LETTURA_BLOCCATA = 25;

/**
 * Vero se la lettura parte (il lavoro pianificato la chiama) ma non finisce
 * mai: di solito la chiave ORDINI_SITO_KEY della funzione non e' uguale a
 * quella nel caveau, e la funzione risponde "non autorizzato" senza poterlo
 * scrivere da nessuna parte.
 */
export function letturaBloccata(stato: StatoLetturaOrdini | null, adesso: Date): boolean {
  if (!stato?.avviato_at || stato.errore) return false;
  const avviata = Date.parse(stato.avviato_at);
  const finita = stato.finito_at ? Date.parse(stato.finito_at) : Number.NEGATIVE_INFINITY;
  const soglia = MINUTI_LETTURA_BLOCCATA * 60_000;
  return avviata - finita > soglia && adesso.getTime() - avviata < 24 * 3600_000;
}

/** Un'email del biglietto in PDF, come la legge la scheda (email_messages con biglietto_id). */
export interface InvioBiglietto {
  contact_id: string;
  status: string;
  sent_at: string | null;
  created_at: string;
  error: string | null;
}

/**
 * Com'e' andato il PDF del biglietto, dall'ultima email mandata al
 * partecipante (la copia a chi ha pagato non conta). Null se il biglietto non
 * e' registrato: non c'e' ancora niente da mandare.
 */
export function statoPdf(
  registrato: boolean,
  partecipanteId: string | null,
  invii: InvioBiglietto[],
): StatoPdf | null {
  if (!registrato) return null;
  const ultimo = invii
    .filter((i) => i.contact_id === partecipanteId)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  if (!ultimo) return { stato: 'in_attesa' };
  switch (ultimo.status) {
    case 'in_coda':
    case 'in_invio':
      return { stato: 'in_coda', errore: ultimo.error };
    case 'inviato':
    case 'consegnato':
      return { stato: 'inviato', quando: ultimo.sent_at };
    case 'annullato':
      return { stato: 'annullato' };
    case 'simulato':
      return { stato: 'simulato' };
    default:
      return { stato: 'errore', errore: ultimo.error };
  }
}
